/** Build inline display rows comparing before/after table data. */

export function titleColumnName(schema) {
  const cols = schema || [];
  const marked = cols.find((col) => col?.title && col?.name);
  if (marked) return marked.name;
  return cols[0]?.name || "";
}

export function mergeSchema(beforeSchema, afterSchema) {
  const seen = new Set();
  const merged = [];
  for (const col of [...(afterSchema || []), ...(beforeSchema || [])]) {
    if (!col?.name || seen.has(col.name)) continue;
    seen.add(col.name);
    merged.push(col);
  }
  return merged;
}

export function cellEmpty(value) {
  return value == null || value === "";
}

/** empty→full added, full→empty deleted, both-valued change updated, else null. */
export function classifyCell(before, after) {
  const bEmpty = cellEmpty(before);
  const aEmpty = cellEmpty(after);
  if (bEmpty && aEmpty) return null;
  if (bEmpty) return "added";
  if (aEmpty) return "deleted";
  if (before === after) return null;
  return "updated";
}

function rowKey(row) {
  // Stable key for sequence alignment (mirrors backend utils_data._row_key).
  return JSON.stringify(row, Object.keys(row || {}).sort());
}

/**
 * SequenceMatcher-style opcodes over two hash arrays.
 * Returns [tag, i1, i2, j1, j2] like Python difflib.
 */
function sequenceOpcodes(a, b) {
  const blocks = [];
  const match = (alo, ahi, blo, bhi) => {
    const bIndex = new Map();
    for (let j = blo; j < bhi; j += 1) {
      const key = b[j];
      let list = bIndex.get(key);
      if (!list) {
        list = [];
        bIndex.set(key, list);
      }
      list.push(j);
    }
    let bestI = alo;
    let bestJ = blo;
    let bestSize = 0;
    for (let i = alo; i < ahi; i += 1) {
      const js = bIndex.get(a[i]);
      if (!js) continue;
      for (let n = 0; n < js.length; n += 1) {
        const j = js[n];
        if (j < blo) continue;
        if (j >= bhi) break;
        let size = 0;
        while (
          i + size < ahi
          && j + size < bhi
          && a[i + size] === b[j + size]
        ) {
          size += 1;
        }
        if (size > bestSize) {
          bestI = i;
          bestJ = j;
          bestSize = size;
        }
      }
    }
    if (bestSize === 0) return;
    if (alo < bestI && blo < bestJ) match(alo, bestI, blo, bestJ);
    blocks.push([bestI, bestJ, bestSize]);
    if (bestI + bestSize < ahi && bestJ + bestSize < bhi) {
      match(bestI + bestSize, ahi, bestJ + bestSize, bhi);
    }
  };
  match(0, a.length, 0, b.length);
  blocks.push([a.length, b.length, 0]);

  const opcodes = [];
  let i = 0;
  let j = 0;
  for (let n = 0; n < blocks.length; n += 1) {
    const [ai, bj, size] = blocks[n];
    if (i < ai && j < bj) opcodes.push(["replace", i, ai, j, bj]);
    else if (i < ai) opcodes.push(["delete", i, ai, j, bj]);
    else if (j < bj) opcodes.push(["insert", i, ai, j, bj]);
    if (size) {
      opcodes.push(["equal", ai, ai + size, bj, bj + size]);
      i = ai + size;
      j = bj + size;
    }
  }
  return opcodes;
}

/**
 * Sequence-aligned rows for the table grid.
 * @returns {{ change: 'same'|'added'|'deleted'|'updated', data: object, before?: object, index: number|string }[]}
 */
export function buildDiffRows(beforeRows, afterRows) {
  const before = beforeRows || [];
  const after = afterRows || [];
  const beforeKeys = before.map(rowKey);
  const afterKeys = after.map(rowKey);
  const rows = [];

  for (const [tag, i1, i2, j1, j2] of sequenceOpcodes(beforeKeys, afterKeys)) {
    if (tag === "equal") {
      for (let j = j1; j < j2; j += 1) {
        rows.push({ change: "same", data: after[j], index: j });
      }
      continue;
    }
    if (tag === "delete") {
      for (let i = i1; i < i2; i += 1) {
        rows.push({
          change: "deleted",
          data: before[i],
          before: before[i],
          index: `del-${i}`,
        });
      }
      continue;
    }
    if (tag === "insert") {
      for (let j = j1; j < j2; j += 1) {
        rows.push({ change: "added", data: after[j], index: j });
      }
      continue;
    }
    // replace: pair shared length as updates, remainder as add/delete
    const paired = Math.min(i2 - i1, j2 - j1);
    for (let k = 0; k < paired; k += 1) {
      const oldRow = before[i1 + k];
      const newRow = after[j1 + k];
      if (JSON.stringify(oldRow) === JSON.stringify(newRow)) {
        rows.push({ change: "same", data: newRow, index: j1 + k });
      } else {
        rows.push({
          change: "updated",
          data: newRow,
          before: oldRow,
          index: j1 + k,
        });
      }
    }
    for (let i = i1 + paired; i < i2; i += 1) {
      rows.push({
        change: "deleted",
        data: before[i],
        before: before[i],
        index: `del-${i}`,
      });
    }
    for (let j = j1 + paired; j < j2; j += 1) {
      rows.push({ change: "added", data: after[j], index: j });
    }
  }
  return rows;
}

export function formatTableSize(bytes) {
  if (bytes == null || bytes === 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) {
    const kb = bytes / 1024;
    return `${kb >= 10 ? Math.round(kb) : kb.toFixed(1)} KB`;
  }
  const mb = bytes / (1024 * 1024);
  return `${mb >= 10 ? Math.round(mb) : mb.toFixed(1)} MB`;
}

export function compareCellValues(a, b, direction = "asc") {
  const left = a == null ? "" : String(a);
  const right = b == null ? "" : String(b);
  const leftNum = Number(left);
  const rightNum = Number(right);
  let result = 0;
  if (left !== "" && right !== "" && !Number.isNaN(leftNum) && !Number.isNaN(rightNum)) {
    result = leftNum - rightNum;
  } else {
    result = left.localeCompare(right, undefined, { numeric: true, sensitivity: "base" });
  }
  return direction === "desc" ? -result : result;
}
